import { getConsultationById, getPrescriptionById } from "@/core/clinical/clinical-store";
import type { Prescription, PrescriptionLine } from "@/core/clinical/types";
import { getPatientById } from "@/core/patients/patient-store";
import { getActivePartnershipForInsurer } from "./partnership-store";
import {
  createCoverageCase,
  listCoverageCases,
  patchCoverageCase,
} from "./coverage-case-store";
import { submitCoverageCaseToHub } from "./hub-submission";
import {
  evaluateInsurerValidationNeed,
  type InsurerValidationContext,
} from "./insurer-rules-store";
import type { CoverageCase, CoverageCaseKind } from "./types";

function estimatePrescriptionAmount(rx: Prescription): number {
  return rx.lines.reduce((sum: number, line: PrescriptionLine) => sum + line.quantity * 5_000, 0);
}

function buildValidationContext(
  kind: CoverageCaseKind,
  patientId: string,
  options: {
    prescriptionId?: string;
    consultationId?: string;
    examLabel?: string;
    amount?: number;
  },
): InsurerValidationContext | null {
  const patient = getPatientById(patientId);
  if (!patient?.insurerId) return null;

  let motif = "";
  let diagnostic = "";
  let acts = "";
  let prescriptionLines: InsurerValidationContext["prescriptionLines"];
  let estimatedAmount = options.amount ?? 0;

  if (options.consultationId) {
    const consultation = getConsultationById(options.consultationId);
    if (consultation) {
      motif = consultation.motif;
      diagnostic = consultation.diagnostic;
      acts = consultation.acts;
    }
  }

  if (options.prescriptionId) {
    const rx = getPrescriptionById(options.prescriptionId);
    if (rx) {
      prescriptionLines = rx.lines.map((l) => ({
        label: l.productLabel,
        quantity: l.quantity,
      }));
      estimatedAmount = estimatedAmount || estimatePrescriptionAmount(rx);
      if (!options.consultationId && rx.consultationId) {
        const consultation = getConsultationById(rx.consultationId);
        if (consultation) {
          motif = consultation.motif;
          diagnostic = consultation.diagnostic;
          acts = consultation.acts;
        }
      }
    }
  }

  return {
    kind,
    motif,
    diagnostic,
    acts,
    examLabel: options.examLabel,
    prescriptionLines,
    estimatedAmount,
  };
}

export function validationRequiredForPrescription(prescriptionId: string): {
  required: boolean;
  reasons: string[];
  insurerId: string | null;
} {
  const rx = getPrescriptionById(prescriptionId);
  if (!rx) return { required: false, reasons: [], insurerId: null };
  const patient = getPatientById(rx.patientId);
  if (!patient?.insurerId) return { required: false, reasons: [], insurerId: null };

  const ctx = buildValidationContext("prescription", rx.patientId, {
    prescriptionId: rx.id,
    consultationId: rx.consultationId,
  });
  if (!ctx) return { required: false, reasons: [], insurerId: null };

  const result = evaluateInsurerValidationNeed(patient.insurerId, ctx);
  return { ...result, insurerId: patient.insurerId };
}

export function ensureCoverageCaseForPrescription(prescriptionId: string): CoverageCase | null {
  const rx = getPrescriptionById(prescriptionId);
  if (!rx) return null;

  const check = validationRequiredForPrescription(prescriptionId);
  if (!check.required || !check.insurerId) return null;

  const existing = listCoverageCases().find(
    (c) => c.prescriptionId === prescriptionId && c.kind === "prescription",
  );
  if (existing) return existing;

  const patient = getPatientById(rx.patientId);
  if (!patient) return null;

  const amount = estimatePrescriptionAmount(rx);
  return createCoverageCase({
    insurerId: check.insurerId,
    agentId: patient.insurerAgentId,
    patientId: rx.patientId,
    prescriptionId: rx.id,
    kind: "prescription",
    label: `Ordonnance — ${rx.lines.length} ligne(s)`,
    amount,
    memberNumber: patient.memberNumber,
    policyNumber: patient.policyNumber,
  });
}

export async function submitPrescriptionToInsuranceHub(prescriptionId: string): Promise<{
  importUrl: string;
  caseId: string;
  via: "api" | "url";
  error?: string;
} | null> {
  const coverageCase = ensureCoverageCaseForPrescription(prescriptionId);
  if (!coverageCase) return null;

  const patient = getPatientById(coverageCase.patientId);
  if (!patient?.insurerId) return null;
  if (!getActivePartnershipForInsurer(patient.insurerId)) return null;

  const result = await submitCoverageCaseToHub(coverageCase.id);
  if (!result) return null;
  return { importUrl: result.importUrl, caseId: coverageCase.id, via: result.via, error: result.error };
}

export function canSubmitPrescriptionToHub(prescriptionId: string): {
  canSubmit: boolean;
  reasons: string[];
  validationRequired: boolean;
  hasPartnership: boolean;
  coverageCase: CoverageCase | null;
} {
  const validation = validationRequiredForPrescription(prescriptionId);
  const rx = getPrescriptionById(prescriptionId);
  const insurerId = validation.insurerId;
  const hasPartnership = insurerId ? !!getActivePartnershipForInsurer(insurerId) : false;
  const coverageCase = getCoverageCaseForPrescription(prescriptionId);

  if (!validation.required) {
    return {
      canSubmit: false,
      reasons: [],
      validationRequired: false,
      hasPartnership,
      coverageCase,
    };
  }

  if (!hasPartnership) {
    return {
      canSubmit: false,
      reasons: ["Partenariat hub non actif — configurez-le dans Assurance → Partenariats."],
      validationRequired: true,
      hasPartnership: false,
      coverageCase,
    };
  }

  if (!rx || rx.lines.length === 0) {
    return {
      canSubmit: false,
      reasons: ["Ajoutez des lignes à l'ordonnance."],
      validationRequired: true,
      hasPartnership,
      coverageCase,
    };
  }

  return {
    canSubmit: true,
    reasons: validation.reasons,
    validationRequired: true,
    hasPartnership,
    coverageCase: coverageCase ?? ensureCoverageCaseForPrescription(prescriptionId),
  };
}

export function getCoverageCaseForPrescription(prescriptionId: string): CoverageCase | null {
  return (
    listCoverageCases().find(
      (c) => c.prescriptionId === prescriptionId && c.kind === "prescription",
    ) ?? null
  );
}

export interface InsurerActGateResult {
  allowed: boolean;
  message: string;
  validationRequired: boolean;
  reasons: string[];
}

function findApprovedActCoverage(
  patientId: string,
  kind: CoverageCaseKind,
  actLabel: string,
): CoverageCase | null {
  const hint = actLabel.trim().toLowerCase();
  if (!hint) return null;
  return (
    listCoverageCases({ patientId, status: "approved" }).find((c) => {
      if (c.kind !== kind) return false;
      const label = c.label.toLowerCase();
      return label.includes(hint) || hint.includes(label.slice(0, Math.min(label.length, 24)));
    }) ?? null
  );
}

function buildActGate(
  patientId: string,
  kind: CoverageCaseKind,
  actLabel: string,
  consultationId?: string | null,
): InsurerActGateResult {
  const patient = getPatientById(patientId);
  if (!patient?.insurerId) {
    return { allowed: true, message: "", validationRequired: false, reasons: [] };
  }

  const ctx = buildValidationContext(kind, patientId, {
    consultationId: consultationId ?? undefined,
    examLabel: actLabel,
  });
  if (!ctx) {
    return { allowed: true, message: "", validationRequired: false, reasons: [] };
  }

  const result = evaluateInsurerValidationNeed(patient.insurerId, ctx);
  if (!result.required) {
    return { allowed: true, message: "", validationRequired: false, reasons: [] };
  }

  if (findApprovedActCoverage(patientId, kind, actLabel)) {
    return { allowed: true, message: "", validationRequired: true, reasons: result.reasons };
  }

  return {
    allowed: false,
    validationRequired: true,
    reasons: result.reasons,
    message: `Accord assureur requis pour « ${actLabel} ». Validez le dossier dans Assurance → Prises en charge (ou soumettez au hub) avant de créer la demande.`,
  };
}

export function checkLabOrderInsurerGate(input: {
  patientId: string;
  testName: string;
  consultationId?: string | null;
}): InsurerActGateResult {
  return buildActGate(input.patientId, "laboratory", input.testName, input.consultationId);
}

export function checkImagingExamInsurerGate(input: {
  patientId: string;
  examType: string;
  bodyPart?: string;
  consultationId?: string | null;
}): InsurerActGateResult {
  const label = [input.examType, input.bodyPart].filter(Boolean).join(" — ");
  return buildActGate(input.patientId, "imaging", label, input.consultationId);
}

export function ensureCoverageCaseForLabOrder(input: {
  patientId: string;
  testName: string;
  consultationId?: string | null;
  labOrderId?: string;
}): CoverageCase | null {
  const gate = buildActGate(input.patientId, "laboratory", input.testName, input.consultationId);
  if (!gate.validationRequired) return null;

  const patient = getPatientById(input.patientId);
  if (!patient?.insurerId) return null;

  const existing = listCoverageCases().find(
    (c) =>
      c.patientId === input.patientId &&
      c.kind === "laboratory" &&
      (input.labOrderId ? c.labOrderId === input.labOrderId : c.label.toLowerCase().includes(input.testName.toLowerCase().slice(0, 20))),
  );
  if (existing) return existing;

  return createCoverageCase({
    insurerId: patient.insurerId,
    agentId: patient.insurerAgentId,
    patientId: input.patientId,
    labOrderId: input.labOrderId ?? null,
    kind: "laboratory",
    label: `Laboratoire — ${input.testName}`,
    memberNumber: patient.memberNumber,
    policyNumber: patient.policyNumber,
  });
}

export function linkCoverageCaseToLabOrder(coverageCaseId: string, labOrderId: string): void {
  patchCoverageCase(coverageCaseId, { labOrderId });
}

export function ensureCoverageCaseForImagingExam(input: {
  patientId: string;
  examLabel: string;
  consultationId?: string | null;
  imagingExamId?: string;
}): CoverageCase | null {
  const gate = buildActGate(input.patientId, "imaging", input.examLabel, input.consultationId);
  if (!gate.validationRequired) return null;

  const patient = getPatientById(input.patientId);
  if (!patient?.insurerId) return null;

  const existing = listCoverageCases().find(
    (c) =>
      c.patientId === input.patientId &&
      c.kind === "imaging" &&
      (input.imagingExamId
        ? c.imagingExamId === input.imagingExamId
        : c.label.toLowerCase().includes(input.examLabel.toLowerCase().slice(0, 20))),
  );
  if (existing) return existing;

  return createCoverageCase({
    insurerId: patient.insurerId,
    agentId: patient.insurerAgentId,
    patientId: input.patientId,
    imagingExamId: input.imagingExamId ?? null,
    kind: "imaging",
    label: `Imagerie — ${input.examLabel}`,
    memberNumber: patient.memberNumber,
    policyNumber: patient.policyNumber,
  });
}

export function linkCoverageCaseToImagingExam(
  coverageCaseId: string,
  imagingExamId: string,
): void {
  patchCoverageCase(coverageCaseId, { imagingExamId });
}

export function prescriptionLineRequiresInsurerValidation(
  prescriptionId: string,
  lineId: string,
): boolean {
  const rx = getPrescriptionById(prescriptionId);
  if (!rx) return false;
  const line = rx.lines.find((l) => l.id === lineId);
  if (!line) return false;
  const patient = getPatientById(rx.patientId);
  if (!patient?.insurerId) return false;

  const ctx = buildValidationContext("prescription", rx.patientId, {
    prescriptionId: rx.id,
    consultationId: rx.consultationId,
  });
  if (!ctx) return false;

  const lineCtx: InsurerValidationContext = {
    ...ctx,
    prescriptionLines: [{ label: line.productLabel, quantity: line.quantity }],
  };
  return evaluateInsurerValidationNeed(patient.insurerId, lineCtx).required;
}
